import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { POLL_PORTAL_MS } from "@/lib/realtime";
import { livePoll, useCalendarLive } from "../api/calendarLive";
import type { PortalSource } from "@/app/routes/client-portal/api/clientPortalApi";

/**
 * A Guest's calendar (a brand's client, logged in). The server answers with the
 * client projection of each month — never the team's month — and every
 * client-visible month of a brand the guest holds is theirs to answer.
 */
export type GuestMonth = {
  planId: string;
  year: number;
  month: number;
};

export const guestMonthsKey = (brandId: string | null) => ["guestCalendar", "months", brandId];

export function useGuestMonths(brandId: string | null) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: guestMonthsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ months: GuestMonth[] }>(`/guest-calendar/${brandId}/months`);
      return res.data.months;
    },
    enabled: !!brandId,
    // The team shares and approves months while this tab sits open; the
    // calendar's realtime hints refresh it, and this polls without them.
    refetchInterval: livePoll(live, POLL_PORTAL_MS),
    refetchOnWindowFocus: !live,
  });
}

/** The client review hooks and panels, pointed at one month through the guest routes. */
export function guestPortalSource(brandId: string, planId: string): PortalSource {
  const base = `/guest-calendar/${brandId}/months/${planId}`;
  return {
    id: `guest:${brandId}:${planId}`,
    get: (resource) => axiosInstance.get(resource === "calendar" ? base : `${base}/${resource}`),
    post: (path, body) => axiosInstance.post(`${base}${path}`, body),
    // Access withdrawn (brand removed) or the month taken back: stop polling.
    isDead: (error) =>
      isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 404),
  };
}
